import { format, isValid, parse } from "date-fns";

import { FilePreviewGrid } from "@bitwarden/web-vault/app/services/DataService/file-upload/file-upload.service";
import { TransactionDirection } from "@bitwarden/web-vault/app/models/enum/transactionDirection";

/**
 * Date format tokens (date-fns) offered when importing a bank statement.
 *
 * Superset of the preference date formats: it also covers text-month statements
 * (e.g. "10 Jun 26" -> "dd MMM yy") and foreign year-first layouts (e.g. Taiwan
 * "26/06/10" -> "yy/MM/dd") so a user in one country can import another country's
 * statement.
 */
export const STATEMENT_DATE_FORMATS: string[] = [
  // numeric, day-first (AU/NZ/EU)
  "dd/MM/yyyy",
  "dd/MM/yy",
  "dd-MM-yyyy",
  "dd-MM-yy",
  // numeric, month-first (US)
  "MM/dd/yyyy",
  "MM/dd/yy",
  // numeric, year-first (ISO / Taiwan / Japan)
  "yyyy-MM-dd",
  "yyyy/MM/dd",
  "yy/MM/dd",
  // text-month
  "dd MMM yyyy",
  "dd MMM yy",
  "dd-MMM-yyyy",
  "dd-MMM-yy",
  "MMM dd yyyy",
  "MMM dd, yyyy",
  "dd MMMM yyyy",
];

/** The canonical date string stored on a TransactionForm. */
const OUTPUT_DATE_FORMAT = "yyyy-MM-dd";

const COMPATIBLE_DATE_FORMATS: Record<string, string[]> = {
  "dd/MM/yyyy": ["d/MM/yyyy", "dd/M/yyyy", "d/M/yyyy"],
  "dd/MM/yy": ["d/MM/yy", "dd/M/yy", "d/M/yy"],
  "dd-MM-yyyy": ["d-MM-yyyy", "dd-M-yyyy", "d-M-yyyy"],
  "dd-MM-yy": ["d-MM-yy", "dd-M-yy", "d-M-yy"],
  "MM/dd/yyyy": ["M/dd/yyyy", "MM/d/yyyy", "M/d/yyyy"],
  "MM/dd/yy": ["M/dd/yy", "MM/d/yy", "M/d/yy"],
  "yyyy-MM-dd": ["yyyy-M-dd", "yyyy-MM-d", "yyyy-M-d"],
  "yyyy/MM/dd": ["yyyy/M/dd", "yyyy/MM/d", "yyyy/M/d"],
  "yy/MM/dd": ["yy/M/dd", "yy/MM/d", "yy/M/d"],
  // text-month: statements often write the day unpadded ("5 Jun 2026"), which
  // parses but fails the round-trip equality check against the padded `dd` format.
  "dd MMM yyyy": ["d MMM yyyy"],
  "dd MMM yy": ["d MMM yy"],
  "dd-MMM-yyyy": ["d-MMM-yyyy"],
  "dd-MMM-yy": ["d-MMM-yy"],
  "MMM dd yyyy": ["MMM d yyyy"],
  "MMM dd, yyyy": ["MMM d, yyyy"],
  "dd MMMM yyyy": ["d MMMM yyyy"],
};

/**
 * How the amount is read from the statement.
 * - `single`: one signed amount column (sign decides direction).
 * - `split`: separate debit (money out) and credit (money in) columns.
 */
export type AmountMode = "single" | "split";

/** Column-mapping selected by the user in the statement-import dialog. */
export type StatementMapping = {
  amountMode: AmountMode;
  /** Used when `amountMode === "single"`. */
  amountIndex: number;
  /** Used when `amountMode === "split"` (money out -> OUT). */
  debitIndex: number;
  /** Used when `amountMode === "split"` (money in -> IN). */
  creditIndex: number;
  dateIndex: number;
  dateFormat: string;
  descriptionIndex: number;
  /** -1 when the statement has no category column. */
  categoryIndex: number;
  /** -1 when the statement has no running-balance column (link-account only, never per-transaction). */
  balanceIndex: number;
};

/** One parsed statement row, ready to preview or turn into a transaction. */
export type MappedStatementRow = {
  /** Zero-based row index in the parsed data rows (header excluded). */
  sourceRowIndex: number;
  /** One-based spreadsheet row number (header is row 1). */
  sourceRowNumber: number;
  /** ISO `yyyy-MM-dd`, or "" when the date could not be parsed. */
  date: string;
  /** The raw value of the date cell (shown when parsing failed). */
  rawDate: string;
  description: string;
  /** Absolute amount (sign stripped). */
  amount: number;
  type: TransactionDirection;
  /** Category name from the statement, or "" when not mapped/empty. */
  category: string;
  /** Running balance for this row (link-account use only), or undefined when not mapped. */
  balance?: string;
  /** The currency the amount is expressed in: statement currency before conversion, account currency after. */
  currency?: string;
  /** False when the row could not produce a usable transaction. */
  valid: boolean;
};

/** Statement-level summary derived from the mapped rows, used to prefill the link-account step. */
export type StatementSummary = {
  /** Earliest transaction date (yyyy-MM-dd). */
  fromDate: string;
  /** Latest transaction date (yyyy-MM-dd). */
  toDate: string;
  /** Balance on the earliest-date row (undefined when no balance column mapped). */
  openingBalance?: string;
  /** Balance on the latest-date row (undefined when no balance column mapped). */
  closingBalance?: string;
};

/** Find the first column whose header matches `regex`, else -1. */
export const detectColumn = (headers: string[], regex: RegExp): number =>
  (headers ?? []).findIndex((h) => regex.test((h ?? "").trim()));

export const detectDateColumn = (headers: string[]): number =>
  detectColumn(headers, /date|posted|transaction\s*date/i);

/** A single signed amount column (not a debit/credit pair). */
// "amount"/"value" as whole words, excluding "...Date" so "Value Date" isn't mistaken for the amount.
export const detectAmountColumn = (headers: string[]): number =>
  detectColumn(headers, /^(?!.*\bdate\b)(?=.*\b(?:amount|value)\b)/i);

/** Money-out column (maps to OUT). */
export const detectDebitColumn = (headers: string[]): number =>
  detectColumn(headers, /debit|withdraw|withdrawal|paid\s*out|money\s*out|\bout\b/i);

/** Money-in column (maps to IN). */
export const detectCreditColumn = (headers: string[]): number =>
  detectColumn(headers, /credit|deposit|paid\s*in|money\s*in|\bin\b/i);

export const detectDescriptionColumn = (headers: string[]): number => {
  const preferred = detectColumn(headers, /description|details|narrative|particulars|memo/i);
  return preferred >= 0 ? preferred : headers?.length ? 0 : -1;
};

export const detectCategoryColumn = (headers: string[]): number =>
  detectColumn(headers, /category|categories/i);

/** Running-balance column (link-account use only). */
export const detectBalanceColumn = (headers: string[]): number => detectColumn(headers, /balance/i);

/** Currency/CCY column — used only to guard against FX mismatch (import never converts). */
export const detectCurrencyColumn = (headers: string[]): number =>
  detectColumn(headers, /currency|\bccy\b/i);

/** Plausible range for a statement date; outside this a parse is treated as garbage. */
const MIN_STATEMENT_YEAR = 1900;
const MAX_STATEMENT_YEAR = 2100;

/**
 * Strip a trailing time component from a date cell, leaving the date intact.
 * Only removes ISO "T"/"@" times and " HH:MM[:SS][ AM/PM]" — NOT all whitespace,
 * so text-month dates ("10 Jun 2026") survive.
 */
const cleanDateCell = (value: string): string =>
  (value ?? "")
    .trim()
    .replace(/[T@]\d.*$/i, "")
    .replace(/\s+\d{1,2}:\d{2}(:\d{2})?(\s*[AP]M)?$/i, "")
    .trim();

const getCompatibleDateFormats = (dateFormat: string): string[] => [
  dateFormat,
  ...(COMPATIBLE_DATE_FORMATS[dateFormat] ?? []),
];

const parseWithDateFormat = (value: string, dateFormat: string): Date | null => {
  for (const fmt of getCompatibleDateFormats(dateFormat)) {
    const parsed = parse(value, fmt, new Date());
    if (isValid(parsed) && format(parsed, fmt).toLowerCase() === value.toLowerCase()) {
      return parsed;
    }
  }
  return null;
};

/**
 * Pick the date format that parses the most non-empty samples.
 * Returns null when nothing parses (caller falls back to the user's preference).
 */
export const detectDateFormat = (
  samples: string[],
  formats: string[] = STATEMENT_DATE_FORMATS,
): string | null => {
  const cleaned = (samples ?? []).map(cleanDateCell).filter((s) => s !== "");
  if (cleaned.length === 0) {
    return null;
  }

  let best: string | null = null;
  let bestHits = 0;
  for (const fmt of formats) {
    let hits = 0;
    for (const sample of cleaned) {
      if (parseWithDateFormat(sample, fmt)) {
        hits++;
      }
    }
    if (hits > bestHits) {
      bestHits = hits;
      best = fmt;
    }
  }
  return best;
};

/** Parse a date cell with the chosen format into `yyyy-MM-dd`, or "" on failure. */
export const parseStatementDate = (value: string, dateFormat: string): string => {
  const cleaned = cleanDateCell(value);
  if (!cleaned || !dateFormat) {
    return "";
  }
  const parsed = parseWithDateFormat(cleaned, dateFormat);
  if (!parsed) {
    return "";
  }
  // Reject lenient mis-parses: a short year against a `yyyy` format yields an
  // implausible year (e.g. "26/6/10" -> 0010), which would sort as the earliest
  // row and corrupt the statement start date + opening balance.
  const year = parsed.getFullYear();
  if (year < MIN_STATEMENT_YEAR || year > MAX_STATEMENT_YEAR) {
    return "";
  }
  return format(parsed, OUTPUT_DATE_FORMAT);
};

/**
 * Normalise a numeric cell to a JS-parseable string with `.` as the decimal
 * separator, stripping currency symbols and grouping separators. Handles both
 * US/AU (`1,234.56`) and EU (`1.234,56`) conventions. Returns "" when the cell
 * carries no digits.
 *
 * Rules:
 * - Both `.` and `,` present: the right-most is the decimal point, the other is
 *   the thousands separator (`1.234,56` -> `1234.56`, `1,234.56` -> `1234.56`).
 * - A single separator type repeated: grouping only (`1.234.567` -> `1234567`).
 * - A lone separator with exactly 3 trailing digits: grouping (`1,234`/`1.234`
 *   -> `1234`); otherwise the decimal point (`12,5` -> `12.5`, `9.99` -> `9.99`).
 *   EXCEPTION: when `decimalPlaces === 3` (3-decimal currencies: KWD, BHD, JOD,
 *   OMR, TND, …) a lone-separator 3-digit tail is the fraction, not grouping
 *   (`12.500` -> `12.500`), so those amounts aren't inflated ~1000x.
 * @param decimalPlaces optional fraction-digit count of the target currency
 */
export const normalizeNumberString = (raw: string, decimalPlaces?: number): string => {
  const core = (raw ?? "").replace(/[^0-9.,]/g, "");
  if (core === "") {
    return "";
  }
  const hasDot = core.includes(".");
  const hasComma = core.includes(",");

  if (hasDot && hasComma) {
    const decimalSep = core.lastIndexOf(".") > core.lastIndexOf(",") ? "." : ",";
    const thousandsSep = decimalSep === "." ? "," : ".";
    return core.split(thousandsSep).join("").replace(decimalSep, ".");
  }

  const sep = hasComma ? "," : hasDot ? "." : "";
  if (sep === "") {
    return core;
  }
  const parts = core.split(sep);
  // Repeated separator -> grouping only; otherwise a lone 3-digit tail reads as
  // grouping, anything else as the decimal point.
  if (parts.length > 2) {
    return parts.join("");
  }
  // 3-digit tail is grouping (`1,234` -> `1234`) UNLESS the target currency uses
  // 3 decimal places, where it's the fraction (`1.234` KWD -> `1.234`).
  if (parts[1].length === 3 && decimalPlaces !== 3) {
    return parts.join("");
  }
  return `${parts[0]}.${parts[1]}`;
};

/**
 * Fraction-digit count for an ISO currency code via Intl (2 for most, 3 for
 * KWD/BHD/…, 0 for JPY/…). Returns undefined for unknown/blank codes.
 */
export const currencyDecimalPlaces = (currency?: string): number | undefined => {
  if (!currency) {
    return undefined;
  }
  try {
    return new Intl.NumberFormat("en", {
      style: "currency",
      currency,
    }).resolvedOptions().maximumFractionDigits;
  } catch {
    return undefined;
  }
};

/**
 * Parse an amount cell: strip currency symbols, grouping separators and the
 * sign (locale-aware decimal handling). The sign decides the direction
 * (negative -> OUT, otherwise IN).
 */
export const parseStatementAmount = (
  value: string,
  decimalPlaces?: number,
): { amount: number; type: TransactionDirection } | null => {
  if (value == null) {
    return null;
  }
  const raw = String(value).trim();
  if (raw === "") {
    return null;
  }
  // Treat a leading minus or parenthesised value as negative.
  const isNegative = /^\(.*\)$/.test(raw) || /-/.test(raw);
  const numeric = normalizeNumberString(raw, decimalPlaces);
  if (numeric === "") {
    return null;
  }
  const amount = Math.abs(parseFloat(numeric));
  // Drop zero/blank-amount rows (e.g. "balance brought forward" lines) so they
  // can't be mistaken for the earliest transaction. Matches split-mode behaviour.
  if (isNaN(amount) || amount === 0) {
    return null;
  }
  return {
    amount,
    type: isNegative ? TransactionDirection.Out : TransactionDirection.In,
  };
};

/** Parse a balance cell to a signed numeric string (symbols/commas stripped), or undefined. */
export const parseBalanceValue = (value: string, decimalPlaces?: number): string | undefined => {
  if (value == null) {
    return undefined;
  }
  const raw = String(value).trim();
  if (raw === "") {
    return undefined;
  }
  const negative = /^\(.*\)$/.test(raw) || /-/.test(raw);
  const numeric = normalizeNumberString(raw, decimalPlaces);
  if (numeric === "") {
    return undefined;
  }
  const parsed = parseFloat(numeric);
  if (isNaN(parsed)) {
    return undefined;
  }
  return (negative ? -Math.abs(parsed) : Math.abs(parsed)).toString();
};

/** Parse the magnitude of an amount cell (sign/symbols stripped), or null. */
export const parseAmountMagnitude = (value: string, decimalPlaces?: number): number | null => {
  if (value == null) {
    return null;
  }
  const numeric = normalizeNumberString(String(value), decimalPlaces);
  if (numeric === "") {
    return null;
  }
  const amount = Math.abs(parseFloat(numeric));
  return isNaN(amount) || amount === 0 ? null : amount;
};

/**
 * Resolve amount + direction from a debit (money out) / credit (money in) pair.
 * Whichever column carries a non-zero value wins; debit takes precedence.
 */
export const parseSplitAmount = (
  debitCell: string,
  creditCell: string,
  decimalPlaces?: number,
): { amount: number; type: TransactionDirection } | null => {
  const debit = parseAmountMagnitude(debitCell, decimalPlaces);
  if (debit != null) {
    return { amount: debit, type: TransactionDirection.Out };
  }
  const credit = parseAmountMagnitude(creditCell, decimalPlaces);
  if (credit != null) {
    return { amount: credit, type: TransactionDirection.In };
  }
  return null;
};

/**
 * Turn the grid + mapping into preview rows. Rows with no usable amount are
 * dropped (statement totals / blank lines); balance columns are never read.
 */
export const buildMappedRows = (
  grid: FilePreviewGrid,
  mapping: StatementMapping,
  decimalPlaces?: number,
): MappedStatementRow[] => {
  const rows: MappedStatementRow[] = [];
  for (const [sourceRowIndex, cells] of (grid?.rows ?? []).entries()) {
    const parsedAmount =
      mapping.amountMode === "split"
        ? parseSplitAmount(cells[mapping.debitIndex], cells[mapping.creditIndex], decimalPlaces)
        : parseStatementAmount(cells[mapping.amountIndex], decimalPlaces);
    if (!parsedAmount) {
      continue;
    }
    const rawDate = (cells[mapping.dateIndex] ?? "").trim();
    const date = parseStatementDate(rawDate, mapping.dateFormat);
    const description = (cells[mapping.descriptionIndex] ?? "").trim();
    const category = mapping.categoryIndex >= 0 ? (cells[mapping.categoryIndex] ?? "").trim() : "";
    const balance =
      mapping.balanceIndex >= 0
        ? parseBalanceValue(cells[mapping.balanceIndex], decimalPlaces)
        : undefined;

    rows.push({
      sourceRowIndex,
      sourceRowNumber: sourceRowIndex + 2,
      date,
      rawDate,
      description,
      amount: parsedAmount.amount,
      type: parsedAmount.type,
      category,
      balance,
      valid: date !== "",
    });
  }
  return rows;
};

/** Below this row count, outlier filtering is skipped (too few rows for stable quartiles). */
const MIN_ROWS_FOR_OUTLIER_FILTER = 8;
/** Standard Tukey fence multiplier for flagging IQR outliers. */
const IQR_FENCE_MULTIPLIER = 1.5;

/** Midnight timestamp (ms) for a yyyy-MM-dd date string. */
const dateToTimestamp = (isoDate: string): number => new Date(`${isoDate}T00:00:00`).getTime();

/** Linear-interpolated quantile of an ascending numeric array. */
const quantile = (sortedAsc: number[], p: number): number => {
  const idx = (sortedAsc.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  return sortedAsc[lo] + (sortedAsc[hi] - sortedAsc[lo]) * (idx - lo);
};

/**
 * Drop rows whose date is a statistical outlier (outside the Tukey IQR fences),
 * so a single mistyped year (e.g. "09/05/2001" instead of "2022") can't become
 * the earliest/latest row and corrupt the prefilled date range + opening balance.
 * The IQR scales with the real spread, so a genuinely wide statement is untouched.
 * Returns the input unchanged when there are too few rows, no spread, or the
 * filter would remove everything.
 */
const withoutDateOutliers = (rows: MappedStatementRow[]): MappedStatementRow[] => {
  if (rows.length < MIN_ROWS_FOR_OUTLIER_FILTER) {
    return rows;
  }
  const timestamps = rows.map((row) => dateToTimestamp(row.date)).sort((a, b) => a - b);
  const q1 = quantile(timestamps, 0.25);
  const q3 = quantile(timestamps, 0.75);
  const iqr = q3 - q1;
  if (iqr === 0) {
    return rows;
  }
  const lower = q1 - IQR_FENCE_MULTIPLIER * iqr;
  const upper = q3 + IQR_FENCE_MULTIPLIER * iqr;
  const kept = rows.filter((row) => {
    const t = dateToTimestamp(row.date);
    return t >= lower && t <= upper;
  });
  return kept.length > 0 ? kept : rows;
};

/**
 * Derive statement-level fields (date range + opening/closing balance) from the
 * mapped rows, to prefill the link-account step. Opening balance comes from the
 * earliest-date row, closing from the latest-date row (date min/max, robust to
 * oldest-first or newest-first file ordering). Outlier dates (typo years) are
 * excluded so they can't hijack the range. Returns null when no valid rows.
 */
export const summarizeStatement = (rows: MappedStatementRow[]): StatementSummary | null => {
  const valid = withoutDateOutliers(rows.filter((row) => row.valid && row.date));
  if (valid.length === 0) {
    return null;
  }
  // yyyy-MM-dd sorts lexicographically in chronological order.
  let fromDate = valid[0].date;
  let toDate = valid[0].date;
  for (const row of valid) {
    if (row.date < fromDate) {
      fromDate = row.date;
    }
    if (row.date > toDate) {
      toDate = row.date;
    }
  }
  // Running balances can't be ordered by date alone when several rows share the
  // earliest/latest date, so fall back to the file's own ordering. Bank exports
  // are commonly newest-first (descending); assuming oldest-first picked the
  // wrong intra-day row for both endpoints. Direction is inferred from the first
  // vs last valid row.
  const ascending = valid[0].date <= valid[valid.length - 1].date;
  const openingRow = ascending
    ? valid.find((row) => row.date === fromDate)
    : [...valid].reverse().find((row) => row.date === fromDate);
  const closingRow = ascending
    ? [...valid].reverse().find((row) => row.date === toDate)
    : valid.find((row) => row.date === toDate);
  return {
    fromDate,
    toDate,
    // The earliest row's running balance is the balance AFTER its transaction, so the
    // period opening balance is that minus the transaction (closing needs no adjustment).
    openingBalance: openingBalanceBefore(openingRow),
    closingBalance: closingRow?.balance,
  };
};

/**
 * Back the earliest transaction out of its running balance to get the period opening
 * balance (balanceBefore = balanceAfter - signedAmount). Returns the raw balance when it
 * can't be parsed, and undefined when there's no balance column.
 */
const openingBalanceBefore = (openingRow?: MappedStatementRow): string | undefined => {
  if (openingRow?.balance == null) {
    return undefined;
  }
  const balanceAfter = parseFloat(openingRow.balance);
  if (isNaN(balanceAfter)) {
    return openingRow.balance;
  }
  const signed =
    openingRow.type === TransactionDirection.Out ? -openingRow.amount : openingRow.amount;
  // Round to cents to avoid binary-float artefacts (e.g. 1599.9999999999998).
  return (Math.round((balanceAfter - signed) * 100) / 100).toString();
};
